// «Сцена» — отдельная страница-студия (владелец 30.09): проект → шоты → кадр → видео → монтаж.
// Контракт — backend/app/api/v1/scene.py; генерации на бэке идут штатными ручками генератора,
// здесь только документы проекта, запуск шагов и опрос статусов.
import { api, API_URL, NETWORK_ERROR_MESSAGE } from './client'
import cameraOptions from '../scene/camera_options.json'
import transitionsSpec from '../scene/transitions.json'

export type SceneStep = 'frame' | 'video'
export type VersionStatus = 'pending' | 'processing' | 'completed' | 'failed' | 'expired'

export interface SceneVersion {
	id: string
	shot_id: string
	step: SceneStep
	source: 'generated' | 'uploaded' | 'extracted'
	status: VersionStatus
	/** Почему не вышло — словами для человека (сервер: scene_failures); ответ провайдера как есть — только сотрудникам. */
	error: string | null
	/** Вид причины: недоступна, не умеет выбранное, проверка содержимого, файл, прочее (старый сервер не отдаёт). */
	error_kind?: 'unavailable' | 'unsupported' | 'moderation' | 'input' | 'other' | null
	/** Деньги неудачной попытки по журналу баланса: списали и вернули (старый сервер не отдаёт). */
	money?: { spent: number; returned: number } | null
	url: string | null
	preview_url: string | null
	expires_at: string | null
	prompt: string | null
	settings: Record<string, unknown>
	duration_seconds: number | null
	start_version_id: string | null
	end_version_id: string | null
	request_id: string | null
	created_at: string | null
}

export interface FrameReference {
	key: string
	preview_key?: string | null
	/** Только в памяти страницы: ссылка на миниатюру после загрузки (в настройки шота не пишется). */
	url?: string | null
}

export interface FrameSettings {
	model?: string
	aspect_ratio?: string
	resolution?: string
	quality?: string
	output_format?: string
	angle?: string
	shot?: string
	lighting?: string
	camera?: string
	lens?: string
	count?: number
	edit_from_active?: boolean
	references?: FrameReference[]
	/** «Кто повторит движение» — персонаж кадра шота «Повторить движение» (id ассета). */
	who?: string | null
	/** Режим промпта: свободный текст или поля (структурный). */
	mode?: 'free' | 'structured'
	fields?: Partial<Record<'subject' | 'location' | 'action' | 'composition' | 'look' | 'mood', string>>
}

/** Ролик с движением шота («Повторить движение»): свой файл и отрезок в нём — start (секунда начала) и length (сколько
 *  целых секунд повторить, правило Kling: от 3; «как на кадре» до 10, «как в ролике» до 30). version_id — только в
 *  запросе: «повторить это движение» из версии видео шота (сервер подставит её файл). url — ссылка на показ (с сервера). */
export interface RefVideo {
	key?: string
	preview_key?: string | null
	version_id?: string
	from_version_id?: string
	name?: string
	seconds?: number | null
	start?: number
	length?: number
	/** Размер кадра ролика (из плеера) — по нему кадр шота берёт форму ролика. */
	width?: number | null
	height?: number | null
	url?: string | null
	preview_url?: string | null
}

export interface VideoSettings {
	/** Режим шага «Видео»: «Повторить движение» (персонаж кадра повторяет движение из ролика) вместо «Оживить кадр». */
	repeat?: boolean
	/** Нейросеть переноса движения (Kling Motion Control) — своя, чтобы выбор «Оживить кадр» не терялся. */
	repeat_model?: string
	/** Поворот и ракурс: как в ролике (до 30 с) или как на кадре (до 10 с). */
	orientation?: 'video' | 'image'
	/** Звук из ролика в результате (по умолчанию — да: это музыка тренда). */
	keep_sound?: boolean
	/** Уточнение текстом к переносу движения (необязательно). */
	repeat_prompt?: string
	ref_video?: RefVideo | null
	model?: string
	resolution?: string
	aspect_ratio?: string
	duration?: number
	audio?: boolean
	/** Голос речи в ролике: модели (как её озвучила нейросеть) или персонажа — его наложим, когда ролик будет готов. */
	/** Голос речи: модели, персонажа (один на всю речь) или персонажей (каждому говорящему свой — «Голоса персонажей»). */
	voice?: 'model' | 'character' | 'characters'
	motion?: string
	end_version_id?: string | null
	/** Обрезка для монтажа; version_id — видео, для которого её выбрали (у другого ролика шота она не действует). */
	trim?: { start: number; end: number | null; version_id?: string | null }
	/** Переход к следующему клипу монтажа (type — по спеке переходов, duration — секунды); нет или null — встык. */
	transition?: SceneTransition | null
	mode?: 'free' | 'structured'
	fields?: Partial<Record<'action' | 'sound', string>>
}

/** Поля структурного режима: подпись и подсказка юзеру (в промпт их собирает бэк — scene_studio.compose_structured). */
/** Поля структурного режима — как у Artlist (06.10): кадр — Subject / Characters, Location, Action, Composition, Style,
 *  Mood; видео — действие и реплики со звуком. Персонажи, локации и предметы — чипами прямо в полях. Ключ «look» — поле
 *  стиля (имя сохранено ради прежних шотов). Промпт из полей собирает бэк — scene_studio.compose_structured. */
export const STRUCTURED_FIELDS: Record<SceneStep, { key: string; label: string; hint: string }[]> = {
	frame: [
		{ key: 'subject', label: 'Объект / аватары', hint: 'Кто или что в центре внимания? Добавьте и второстепенных' },
		{ key: 'location', label: 'Локация', hint: 'Где происходит сцена?' },
		{ key: 'action', label: 'Действие', hint: 'Что делают герои?' },
		{ key: 'composition', label: 'Композиция', hint: 'Где герои находятся в кадре?' },
		{ key: 'look', label: 'Стиль', hint: 'Опишите вид: кино, анимация, 3D' },
		{ key: 'mood', label: 'Настроение', hint: 'Опишите тон, цвета, время суток и свет' },
	],
	video: [
		{ key: 'action', label: 'Действие', hint: 'Что происходит в сцене? Опишите движение' },
		{ key: 'sound', label: 'Реплики и звук', hint: 'Что говорит аватар? Реплики, интонация, фоновые звуки' },
	],
}

/** Предел поля структурного режима (как у бэка: MAX_FIELD_LEN) — с запасом на упоминания ассетов. */
export const MAX_FIELD_LEN = 1000

export interface SceneShot {
	id: string
	project_id: string
	position: number
	title: string
	frame_prompt: string
	frame_settings: FrameSettings
	video_prompt: string
	video_settings: VideoSettings
	active_frame_id: string | null
	active_video_id: string | null
	versions: SceneVersion[]
	updated_at: string | null
}

export interface SceneAssetInfo {
	id: string
	name: string
	kind: 'character' | 'location' | 'prop'
	avatar_url: string | null
	/** Лицо персонажа (вид «Лицо крупно») — для кружков чипов и стопки у «+персонаж»; null — берётся аватар. */
	face_url?: string | null
	is_stock: boolean
}

export interface SceneProjectSummary {
	id: string
	title: string
	is_showcase: boolean
	settings: {
		frame?: FrameSettings
		video?: VideoSettings
		/** Копия шаблона: id примера и карта «шот копии → шот примера» — слой «так в примере» на холсте. */
		from_template?: string
		template_shots?: Record<string, string>
	}
	cover_url: string | null
	shots_count: number
	last_opened_at: string | null
	created_at: string | null
	updated_at: string | null
	/** Только у проекта-примера на входной: аватары ассетов из промптов. */
	featured_assets?: SceneAssetInfo[]
	/** Только у примера: ролики для кольца на входной — первым собранный фильм, дальше видео шотов по порядку. */
	hero_media?: SceneHeroMedia[]
	/** Только у примера/шаблона: подпись карточки шаблона на входной, «на входной» и порядок (админка). */
	description?: string
	featured?: boolean
	order?: number
	readonly?: boolean
}

export interface SceneHome {
	showcase: SceneProjectSummary | null
	/** Шаблоны — карточки как в «Мастерской»; старый сервер поля не отдаёт (тогда шаблон один — пример). */
	templates?: SceneProjectSummary[]
	projects: SceneProjectSummary[]
}

export interface SceneHeroMedia {
	kind: 'film' | 'shot'
	/** Шаблон, которому принадлежит ролик — «Открыть» на карточке кольца ведёт к нему. */
	template_id?: string
	shot_id: string | null
	title: string
	url: string
	poster_url: string | null
	duration_seconds: number | null
}

export interface SceneProject extends SceneProjectSummary {
	shots: SceneShot[]
	assets: Record<string, SceneAssetInfo>
	readonly: boolean
}

export interface SceneExport {
	id: string
	project_id: string
	status: VersionStatus
	url: string | null
	preview_url: string | null
	duration_seconds: number | null
	error: string | null
	plan: { items?: { shot_id: string; version_id: string; title: string; trim_start: number; trim_end: number | null }[] }
	created_at: string | null
	finished_at: string | null
}

export interface ModelCapsBase {
	label: string
	provider: string | null
	aspect_ratios: string[] | null
	resolutions: string[] | null
	price: number | null
	enabled: boolean
	description: string | null
}

export interface FrameModelCaps extends ModelCapsBase {
	max_images: number
	accepts_image: boolean
	quality_levels: string[] | null
	output_formats: string[] | null
}

export interface VideoModelCaps extends ModelCapsBase {
	durations: number[] | null
	duration_range: [number, number] | null
	supports_end_frame: boolean
	supports_audio: boolean
	supports_extend: boolean
	max_extend_seconds: number | null
	/** Берёт ракурсы персонажей, локаций и предметов шота картинками рядом с кадром (Kling Omni). */
	supports_cast_views?: boolean
}

/** Нейросеть переноса движения («Повторить движение»): пределы отрезка ролика — правило модели. */
export interface MotionModelCaps extends ModelCapsBase {
	min_seconds: number
	max_seconds_image: number
	max_seconds_video: number
	/** «Фон из ролика» (Wan Animate Replace, 10.10): человека в ролике заменяет аватар, кадр не нужен, ракурса нет. */
	replace?: boolean
}

export interface CameraOption {
	key: string
	label: string
	phrase: string
}

export interface CameraGroup {
	key: 'angle' | 'shot' | 'lighting' | 'camera' | 'lens' | 'motion'
	step: SceneStep
	label: string
	items: CameraOption[]
}

export interface SceneCaps {
	frame_models: Record<string, FrameModelCaps>
	video_models: Record<string, VideoModelCaps>
	/** Перенос движения из ролика (Kling Motion Control); старый сервер поля не отдаёт — режима нет. */
	motion_models?: Record<string, MotionModelCaps>
	motion_default_model?: string | null
	/** Нейросеть «фона из ролика» (Wan Animate Replace); нет — у повтора только путь через кадр (Kling). */
	replace_model?: string | null
	frame_default_model: string | null
	video_default_model: string | null
	camera_options: CameraGroup[]
	limits: { max_shots: number; max_count: number; max_prompt_len: number; max_references: number; max_versions: number; max_pending?: Partial<Record<SceneStep, number>> }
}

/** Словарь камеры — своя копия спеки (shared/scene/camera_options.json): меню рисуется без запроса к бэку. */
export const CAMERA_GROUPS = (cameraOptions as { groups: CameraGroup[] }).groups
export const cameraGroup = (key: CameraGroup['key']): CameraGroup | undefined => CAMERA_GROUPS.find((g) => g.key === key)
export function cameraLabel(key: CameraGroup['key'], value?: string | null): string {
	const g = cameraGroup(key)
	const item = g?.items.find((i) => i.key === (value || 'auto'))
	return item?.label || 'Авто'
}

const BASE = '/api/v1/scene'

type Ok<T> = { ok: true } & T

export async function sceneHome(): Promise<SceneHome> {
	return api.request<Ok<SceneHome>>(`${BASE}/home`)
}

export async function sceneCaps(): Promise<SceneCaps> {
	return api.request<Ok<SceneCaps>>(`${BASE}/caps`)
}

export async function sceneShowcase(): Promise<SceneProject | null> {
	const r = await api.request<Ok<{ project: SceneProject | null }>>(`${BASE}/showcase`)
	return r.project
}

export async function listSceneProjects(): Promise<SceneProjectSummary[]> {
	return (await api.request<Ok<{ projects: SceneProjectSummary[] }>>(`${BASE}/projects`)).projects
}

export async function createSceneProject(title?: string, settings?: SceneProjectSummary['settings']): Promise<SceneProject> {
	return (await api.request<Ok<{ project: SceneProject }>>(`${BASE}/projects`, { method: 'POST', body: { title, settings } })).project
}

/** Проект из шаблона — копия примера без файлов: шоты с промптами и настройками, путь человек проходит сам. */
export async function createSceneFromTemplate(templateId?: string | null): Promise<SceneProject> {
	return (await api.request<Ok<{ project: SceneProject }>>(`${BASE}/projects/from-template`, { method: 'POST', body: { template_id: templateId ?? null } })).project
}

/** Шаблон целиком, только для чтения (гостю тоже) — /scene/t/<id>; пример входной — sceneShowcase(). */
export async function sceneTemplate(templateId: string): Promise<SceneProject | null> {
	const r = await api.request<Ok<{ project: SceneProject | null }>>(`${BASE}/templates/${encodeURIComponent(templateId)}`)
	return r.project
}

/** touch=false — опрос статусов: проект читаем, но «последнее открытие» не трогаем. */
export async function getSceneProject(id: string, opts: { touch?: boolean } = {}): Promise<SceneProject> {
	return (await api.request<Ok<{ project: SceneProject }>>(`${BASE}/projects/${id}${opts.touch === false ? '?touch=0' : ''}`)).project
}

export async function patchSceneProject(id: string, fields: { title?: string; settings?: SceneProjectSummary['settings'] }): Promise<SceneProjectSummary> {
	return (await api.request<Ok<{ project: SceneProjectSummary }>>(`${BASE}/projects/${id}`, { method: 'PATCH', body: fields })).project
}

export async function deleteSceneProject(id: string): Promise<void> {
	await api.request(`${BASE}/projects/${id}`, { method: 'DELETE' })
}

export async function createSceneShot(projectId: string, afterId?: string | null, title?: string): Promise<SceneShot> {
	return (await api.request<Ok<{ shot: SceneShot }>>(`${BASE}/projects/${projectId}/shots`, { method: 'POST', body: { after_id: afterId ?? null, title } })).shot
}

export async function reorderSceneShots(projectId: string, ids: string[]): Promise<void> {
	await api.request(`${BASE}/projects/${projectId}/reorder`, { method: 'POST', body: { ids } })
}

export async function getSceneShot(id: string): Promise<SceneShot> {
	return (await api.request<Ok<{ shot: SceneShot }>>(`${BASE}/shots/${id}`)).shot
}

export type ShotPatch = Partial<Pick<SceneShot, 'title' | 'frame_prompt' | 'video_prompt' | 'active_frame_id' | 'active_video_id'>> & {
	frame_settings?: FrameSettings
	video_settings?: VideoSettings
}

/** Правка шота для запроса: настройки — без ссылок, живущих только в памяти страницы. */
export function patchBody(fields: ShotPatch): Record<string, unknown> {
	return {
		...fields,
		...(fields.frame_settings ? { frame_settings: settingsBody(fields.frame_settings) } : {}),
		...(fields.video_settings ? { video_settings: settingsBody(fields.video_settings) } : {}),
	}
}

export async function patchSceneShot(id: string, fields: ShotPatch): Promise<SceneShot> {
	return (await api.request<Ok<{ shot: SceneShot }>>(`${BASE}/shots/${id}`, { method: 'PATCH', body: patchBody(fields) })).shot
}

/** Сохранение при уходе со страницы (закрытие вкладки, F5): запрос с keepalive переживает выгрузку документа.
 *  Ответ не нужен — страницы уже не будет. */
export function patchSceneShotOnLeave(id: string, fields: ShotPatch): void {
	const token = api.getToken()
	if (!token) return
	try {
		void fetch(`${API_URL}${BASE}/shots/${id}`, {
			method: 'PATCH', keepalive: true,
			headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
			body: JSON.stringify(patchBody(fields)),
		}).catch(() => { /* страница закрывается */ })
	} catch { /* fetch недоступен — правка останется несохранённой */ }
}

export async function deleteSceneShot(id: string): Promise<void> {
	await api.request(`${BASE}/shots/${id}`, { method: 'DELETE' })
}

export async function duplicateSceneShot(id: string): Promise<SceneShot> {
	return (await api.request<Ok<{ shot: SceneShot }>>(`${BASE}/shots/${id}/duplicate`, { method: 'POST' })).shot
}

/** Новый шот из кадра. Версия-видео: кадр берётся из ролика — на секунде at или последний (at не задан). */
export async function branchSceneShot(id: string, versionId: string, at?: number | null): Promise<SceneShot> {
	return (await api.request<Ok<{ shot: SceneShot }>>(`${BASE}/shots/${id}/branch`, { method: 'POST', body: { version_id: versionId, at: at ?? null } })).shot
}

/** Тело запуска шага: ключ клика (повтор запроса не запускает генерацию второй раз), цена с кнопки (выросла —
 *  отказ вместо списания), промпт и настройки шага такими, какими их видит человек (сохраняются и запускаются
 *  одним запросом — запуск не зависит от того, дошло ли автосохранение). */
export interface StartOptions {
	clientKey: string
	expectedCredits?: number | null
	prompt?: string
	settings?: FrameSettings | VideoSettings
}

/** Настройки для запроса: без полей, живущих только в памяти страницы (ссылки на миниатюры своих фото и на ролик). */
export function settingsBody(settings: FrameSettings | VideoSettings | undefined): Record<string, unknown> | undefined {
	if (!settings) return undefined
	const out: Record<string, unknown> = { ...settings }
	const refs = (settings as FrameSettings).references
	if (refs) out.references = refs.map((r) => ({ key: r.key, preview_key: r.preview_key ?? null }))
	const rv = (settings as VideoSettings).ref_video
	if (rv) {
		const { url: _url, preview_url: _preview, ...stored } = rv
		void _url
		void _preview
		out.ref_video = stored
	}
	return out
}

/** stopped — почему запустилось не всё (или почему генерация провалилась прямо на запуске). */
export async function startSceneFrame(shotId: string, o: StartOptions): Promise<{ versions: SceneVersion[]; stopped: string | null; shot: SceneShot }> {
	return api.request<Ok<{ versions: SceneVersion[]; stopped: string | null; shot: SceneShot }>>(`${BASE}/shots/${shotId}/frame`, {
		method: 'POST',
		// Заголовок ключа включает у клиента повтор после обрыва сети (api/client): сервер по client_key вернёт уже
		// созданные версии, а не запустит генерацию снова.
		headers: { 'X-Idempotency-Key': o.clientKey },
		body: { client_key: o.clientKey, expected_credits: o.expectedCredits ?? null, frame_prompt: o.prompt, frame_settings: settingsBody(o.settings) },
	})
}

export async function startSceneVideo(shotId: string, o: StartOptions & { start_version_id?: string | null; end_version_id?: string | null; extend_version_id?: string | null }): Promise<{ version: SceneVersion; stopped: string | null; shot: SceneShot }> {
	return api.request<Ok<{ version: SceneVersion; stopped: string | null; shot: SceneShot }>>(`${BASE}/shots/${shotId}/video`, {
		method: 'POST',
		headers: { 'X-Idempotency-Key': o.clientKey },
		body: {
			client_key: o.clientKey, expected_credits: o.expectedCredits ?? null, video_prompt: o.prompt, video_settings: settingsBody(o.settings),
			start_version_id: o.start_version_id ?? null, end_version_id: o.end_version_id ?? null, extend_version_id: o.extend_version_id ?? null,
		},
	})
}

/** activate=false — кадр нужен не стартовым (свой конечный кадр): активная версия шота остаётся прежней. */
export async function uploadSceneVersion(shotId: string, step: SceneStep, fileId: string, durationSeconds?: number | null, activate = true): Promise<{ version: SceneVersion; shot: SceneShot }> {
	return api.request<Ok<{ version: SceneVersion; shot: SceneShot }>>(`${BASE}/shots/${shotId}/versions`, {
		method: 'POST', body: { step, file_id: fileId, duration_seconds: durationSeconds ?? null, activate },
	})
}

export async function activateSceneVersion(shotId: string, versionId: string): Promise<SceneShot> {
	return (await api.request<Ok<{ shot: SceneShot }>>(`${BASE}/shots/${shotId}/versions/${versionId}/activate`, { method: 'POST' })).shot
}

/** Ответ провайдера как есть по неудачной версии — только сотрудникам («Подробности» на холсте). */
export interface SceneVersionError { stored: string | null; provider: string | null; code: string | null; request_id: string | null; model: string | null }
export async function getSceneVersionError(shotId: string, versionId: string): Promise<SceneVersionError> {
	return (await api.request<Ok<{ error: SceneVersionError }>>(`${BASE}/shots/${shotId}/versions/${versionId}/error`)).error
}

export async function deleteSceneVersion(shotId: string, versionId: string): Promise<SceneShot> {
	return (await api.request<Ok<{ shot: SceneShot }>>(`${BASE}/shots/${shotId}/versions/${versionId}`, { method: 'DELETE' })).shot
}

/** Закрепить загрузку (иначе файл-референс исчезает через 2 часа): фото кадра или ролик с движением (kind='video'). */
export async function pinSceneUpload(fileId: string, kind?: 'video'): Promise<{ key: string; preview_key: string | null; url: string | null; preview_url: string | null }> {
	return api.request<Ok<{ key: string; preview_key: string | null; url: string | null; preview_url: string | null }>>(`${BASE}/files/pin`, { method: 'POST', body: { file_id: fileId, ...(kind ? { kind } : {}) } })
}

/** Оговорка до запуска: code — вид, short — короткий ярлык для строки оговорок композера, text — полная фраза. */
export interface SceneHint {
	code: string
	short: string
	text: string
}

/** Ответ расчёта цены: сумма, действующие настройки (какими они уйдут в запрос — бэк заменяет значения,
 *  которых у модели нет) и оговорки до запуска словами. */
export interface SceneQuote {
	/** Платная цена одной генерации. */
	unit: number
	count: number
	/** Сколько генераций из count пройдут бесплатно по суточному лимиту тарифа. */
	free_count?: number
	credits: number
	effective: { model: string; resolution?: string | null; quality?: string | null; output_format?: string | null; aspect_ratio?: string | null; count?: number; duration?: number; audio?: boolean; voice?: 'model' | 'character' | 'characters'; orientation?: 'video' | 'image'; keep_sound?: boolean }
	/** «Повторить движение»: пределы отрезка ролика при выбранной ориентации (секунды). */
	repeat?: { min: number; cap: number }
	notes: string[]
	/** Те же оговорки с кодом и коротким ярлыком — строка оговорок композера (старый сервер поля не отдаёт). */
	hints?: SceneHint[]
	/** Голос персонажа (только видео): можно ли его выбрать, чей он и сколько стоит — цена уже в credits. */
	voice?: SceneVoiceQuote
	/** Кто в шоте на шаге «Видео» (как у Artlist — персонажи шота уходят в видео сами): упомянутые в тексте видео, с кем
	 *  нарисован стартовый кадр, упомянутые в промпте кадра. */
	cast?: SceneCastMember[]
	/** Модель возьмёт ракурсы ассетов шота картинками (иначе — словами: имя и описание, личность держит кадр). */
	cast_refs?: boolean
	/** Из чего сложилась цена видео — подсказка на кнопке «Создать»: ролик, звук, голос персонажа. */
	parts?: { key: string; label: string; credits: number }[]
}

export interface SceneCastMember {
	id: string
	name: string
	kind: 'character' | 'location' | 'prop'
	avatar_url: string | null
	face_url?: string | null
	/** Голос персонажа (выбран в его карточке). */
	voice_name: string | null
}

/** Звук видео: по умолчанию включён, как у Artlist («Auto Voice»), — пока его не выключили, если модель умеет звук. */
export function videoAudioOn(settings: VideoSettings, supportsAudio: boolean): boolean {
	return supportsAudio && settings.audio !== false
}

export interface SceneVoiceQuote {
	/** В шоте есть персонаж с голосом. */
	available: boolean
	mode: 'model' | 'character' | 'characters'
	/** Все персонажи шота с голосами — для «Голосов персонажей»; multi_available — их двое и больше. */
	speakers?: { character_id: string | null; character_name: string | null; voice_name: string | null }[]
	multi_available?: boolean
	/** Чей голос зазвучит при «Голосе персонажа» — первый персонаж шота с голосом. */
	character_id: string | null
	character_name: string | null
	voice_name: string | null
	credits: number
	/** Сколько стоил бы голос персонажа при этой длительности — подпись варианта до выбора. */
	price?: number
}

/** Голос у версии видео: какой выбрали при запуске и чем кончилось (воркер пишет итог, когда ролик готов). */
export interface VersionVoice {
	characterName: string
	voiceName: string | null
	/** applied — речь звучит голосом персонажа; skipped / failed — ролик с голосом модели; null — итога ещё нет. */
	status: 'applied' | 'skipped' | 'failed' | null
	note: string | null
}

export function versionVoice(v: SceneVersion | null | undefined): VersionVoice | null {
	if (!v || v.step !== 'video') return null
	const asked = v.settings?.voice as { character_name?: string; voice_name?: string } | undefined
	const result = v.settings?.voice_result as { status?: string; note?: string | null; character_name?: string; voice_name?: string } | undefined
	const name = result?.character_name || asked?.character_name
	if (!name) return null
	const status = result?.status === 'applied' || result?.status === 'skipped' || result?.status === 'failed' ? result.status : null
	return { characterName: name, voiceName: result?.voice_name || asked?.voice_name || null, status, note: result?.note ?? null }
}

/** Цена запуска шага. shotId и assetIds — для оговорок (что нейросеть не получит фото и т. п.);
 *  extend — цена продления готового видео на duration секунд. */
export async function sceneQuote(step: SceneStep, settings: FrameSettings | VideoSettings, opts: { shotId?: string; assetIds?: string[]; extend?: boolean } = {}): Promise<SceneQuote> {
	return api.request<Ok<SceneQuote>>(`${BASE}/quote`, {
		method: 'POST', body: { step, settings: settingsBody(settings), shot_id: opts.shotId ?? null, asset_ids: opts.assetIds ?? [], extend: !!opts.extend },
	})
}

// ── «Повторить движение» (09.10, владелец: «использовать референсное видео, чтобы собрать тренд») ─────────────────
/** Пределы Kling Motion Control — на случай старого сервера без них в возможностях. */
export const REPEAT_MIN_SECONDS = 3
export const REPEAT_MAX_IMAGE = 10
export const REPEAT_MAX_VIDEO = 30
/** «Фон из ролика» берёт отрезок от 2 с (хранимая длина отрезка — от него; Kling свои 3 держит repeatLength). */
export const REPEAT_CLIP_MIN_SECONDS = 2

/** Режим шага «Видео» шота: «Повторить движение» включён и нейросети переноса есть у сервера. */
export function isRepeat(shot: SceneShot, caps?: SceneCaps | null): boolean {
	return !!shot.video_settings.repeat && (!caps || !!caps.motion_models && Object.keys(caps.motion_models).length > 0)
}

/** Сколько секунд движения Kling берёт при ориентации: «как на кадре» — до 10, «как в ролике» — до 30. У «фона из
 *  ролика» ориентации нет — свой предел. */
export function repeatCap(orientation: VideoSettings['orientation'], model?: MotionModelCaps | null): number {
	if (model?.replace) return model.max_seconds_video ?? REPEAT_MAX_VIDEO
	return orientation === 'image' ? model?.max_seconds_image ?? REPEAT_MAX_IMAGE : model?.max_seconds_video ?? REPEAT_MAX_VIDEO
}

/** Самый короткий отрезок, который примет нейросеть повтора: Kling — от 3 с, «фон из ролика» — свой минимум. */
export function repeatMin(model?: MotionModelCaps | null): number {
	return model?.min_seconds ?? REPEAT_MIN_SECONDS
}

/** Нейросеть повтора шота: выбранная (repeat_model) или умолчание «Сцены» (Kling — шоты до «фона из ролика» не меняются). */
export function repeatModelOf(shot: SceneShot, caps?: SceneCaps | null): MotionModelCaps | null {
	const id = shot.video_settings.repeat_model || caps?.motion_default_model || ''
	return caps?.motion_models?.[id] ?? null
}

/** Шот повторяет движение «с фоном из ролика»: аватар встаёт на место человека в ролике, кадр не нужен. */
export function isClipBackground(shot: SceneShot, caps?: SceneCaps | null): boolean {
	return isRepeat(shot, caps) && !!repeatModelOf(shot, caps)?.replace
}

/** Настройки шага «Видео» при входе в «Повторить движение»: новый повтор — сразу «фон из ролика» (аватар в ролик, кадр
 *  не нужен), если такая нейросеть есть; уже начатый повтор и выбранная руками нейросеть не меняются. */
export function repeatOnPatch(shot: SceneShot, caps?: SceneCaps | null): Partial<VideoSettings> {
	if (shot.video_settings.repeat || shot.video_settings.repeat_model || !caps?.replace_model) return { repeat: true }
	return { repeat: true, repeat_model: caps.replace_model }
}

/** Ролик с движением из версии видео шота («Повторить это движение»): сервер подставит её файл (version_id); ссылка и
 *  длина — сразу из версии, чтобы зона показала ролик до ответа сохранения. size — размер кадра ролика, если плеер его
 *  уже знает (по нему кадр шота берёт форму ролика). */
export function refVideoFromVersion(v: SceneVersion, cap: number, size?: { w: number; h: number } | null): RefVideo {
	const seconds = v.duration_seconds != null && v.duration_seconds > 0 ? v.duration_seconds : null
	return {
		version_id: v.id, url: v.url, preview_url: v.preview_url, seconds, start: 0,
		length: seconds != null ? Math.max(REPEAT_CLIP_MIN_SECONDS, Math.min(cap, Math.floor(seconds + 0.05))) : Math.min(cap, REPEAT_MAX_IMAGE),
		name: v.source === 'uploaded' ? 'Ваше видео' : 'Видео шота',
		...(size && size.w > 0 && size.h > 0 ? { width: Math.round(size.w), height: Math.round(size.h) } : {}),
	}
}

/** Какой это ролик: версия видео шота (до ответа сохранения — version_id, после — from_version_id) или свой файл
 *  (ключ). Не меняется, когда сервер подставит ключ версии, — плеер и раскадровка зоны не перезагружаются. */
export function refVideoIdentity(rv: RefVideo | null | undefined): string {
	return rv ? rv.from_version_id || rv.version_id || rv.key || rv.url || '' : ''
}

/** Длина отрезка такой, какой её посчитает сервер (scene_studio.repeat_effective): целые секунды, не меньше минимума
 *  нейросети (Kling — 3 с), не больше предела ориентации и остатка ролика после начала отрезка. */
export function repeatLength(rv: RefVideo | null | undefined, cap: number, min: number = REPEAT_MIN_SECONDS): number {
	let len = Math.round(Number(rv?.length) || Math.min(cap, REPEAT_MAX_IMAGE))
	len = Math.max(min, Math.min(cap, len))
	const seconds = Number(rv?.seconds)
	if (Number.isFinite(seconds) && seconds > 0) {
		const left = Math.floor(seconds - (Number(rv?.start) || 0) + 0.05)
		if (left >= min) len = Math.min(len, left)
	}
	return len
}

/** Переключение режима промпта, как у Artlist: свободный текст → поля структурного режима. Раскладывает
 *  нейросеть-помощник; smart=false — не ответила, разложено простым правилом. */
export async function parseSceneToFields(step: SceneStep, text: string): Promise<{ fields: Record<string, string>; smart: boolean }> {
	return api.request<Ok<{ fields: Record<string, string>; smart: boolean }>>(`${BASE}/parse`, {
		method: 'POST', body: { step, to: 'fields', text },
	})
}

/** Обратное переключение: поля структурного режима → связный текст (той же нейросетью). */
export async function parseSceneToText(step: SceneStep, fields: Record<string, string>): Promise<{ text: string; smart: boolean }> {
	return api.request<Ok<{ text: string; smart: boolean }>>(`${BASE}/parse`, {
		method: 'POST', body: { step, to: 'text', fields },
	})
}

/** Обрезка активного видео шота для монтажа. Обрезку выбирали для конкретного ролика: если активным стал другой
 *  (новая генерация, выбор в истории), она не действует — как на бэке (scene_studio.trim_of). */
export function trimOf(shot: SceneShot): { start: number; end: number | null } {
	const t = shot.video_settings.trim
	if (!t || (t.version_id && t.version_id !== shot.active_video_id)) return { start: 0, end: null }
	const start = Math.max(0, Number(t.start) || 0)
	const end = t.end != null && Number(t.end) > start ? Number(t.end) : null
	return { start, end }
}

/** Конечный кадр видео шота, каким он уйдёт в запрос: готовая версия кадра этого шота, не совпадающая со
 *  стартовым (активным) кадром. Устаревшая ссылка (кадр удалён, истёк) — «не задан». */
/** Конечный кадр видео шота: версия кадра этого шота или другого шота проекта (переход к соседнему шоту, 05.10). */
export function endFrameOf(shot: SceneShot, shots?: readonly SceneShot[]): SceneVersion | null {
	const id = shot.video_settings.end_version_id
	if (!id) return null
	const pool = [shot, ...(shots ?? []).filter((s) => s.id !== shot.id)]
	const v = pool.flatMap((s) => s.versions).find((x) => x.id === id && x.step === 'frame') || null
	return v && v.status === 'completed' && (v.preview_url || v.url) && v.id !== shot.active_frame_id ? v : null
}

export async function startSceneExport(projectId: string): Promise<SceneExport> {
	return (await api.request<Ok<{ export: SceneExport }>>(`${BASE}/projects/${projectId}/export`, { method: 'POST' })).export
}

export async function listSceneExports(projectId: string): Promise<SceneExport[]> {
	return (await api.request<Ok<{ exports: SceneExport[] }>>(`${BASE}/projects/${projectId}/exports`)).exports
}

export async function getSceneExport(id: string): Promise<SceneExport> {
	return (await api.request<Ok<{ export: SceneExport }>>(`${BASE}/exports/${id}`)).export
}

export async function deleteSceneExport(id: string): Promise<void> {
	await api.request(`${BASE}/exports/${id}`, { method: 'DELETE' })
}

/** Ответ-отказ ручек «Сцены»: {ok:false, error, code} у сервиса и detail {code, message} у гейта раздела. */
type SceneFailure = { message?: string; payload?: { error?: string; code?: string }; detail?: unknown }

function detailOf(e: unknown): { code?: string; message?: string } {
	const d = (e as SceneFailure)?.detail
	return d && typeof d === 'object' ? (d as { code?: string; message?: string }) : {}
}

// Сырые тексты браузера и загрузчика, которые человеку ничего не говорят.
const RAW_ERRORS: [RegExp, string][] = [
	[/^(failed to fetch|load failed|networkerror|network request failed)/i, NETWORK_ERROR_MESSAGE],
	[/^upload failed/i, 'Файл не загрузился — попробуйте ещё раз'],
	[/^(request failed|unknown error)$/i, ''],
]

/** Текст ошибки ручки «Сцены» или сети — человеку. */
export function sceneErrorText(e: unknown, fallback = 'Что-то пошло не так'): string {
	const err = e as SceneFailure
	const text = err?.payload?.error || detailOf(e).message || err?.message
	if (typeof text !== 'string' || !text) return fallback
	for (const [re, human] of RAW_ERRORS) if (re.test(text.trim())) return human || fallback
	return text
}

export function sceneErrorCode(e: unknown): string | null {
	const code = (e as SceneFailure)?.payload?.code || detailOf(e).code
	return typeof code === 'string' ? code : null
}

/** Раздел закрыт для этого пользователя (не сотрудник, а «Сцена» ещё не открыта всем) — страницу не показываем. */
export function isSectionClosed(e: unknown): boolean {
	return sceneErrorCode(e) === 'SECTION_CLOSED'
}

/** Активная версия шага (или null). */
export function activeVersion(shot: SceneShot, step: SceneStep): SceneVersion | null {
	const id = step === 'frame' ? shot.active_frame_id : shot.active_video_id
	return (id && shot.versions.find((v) => v.id === id)) || null
}

export function versionsOf(shot: SceneShot, step: SceneStep): SceneVersion[] {
	return shot.versions.filter((v) => v.step === step)
}

export function isBusy(v: SceneVersion | null | undefined): boolean {
	return !!v && (v.status === 'pending' || v.status === 'processing')
}

// ── Переходы между клипами монтажа ─────────────────────────────────────────
// Своя копия спеки (shared/scene/transitions.json, как у словаря камеры): бэк собирает ими склейку (xfade),
// фронт рисует меню. Подписи — отсюда, пределы длительности — тоже.

export interface SceneTransition {
	type: string
	duration: number
}

export const SCENE_TRANSITIONS = transitionsSpec.items as { key: string; group: string; label: string; hint: string; ffmpeg: string }[]
/** Группы панели переходов (как у InShot: «Базовое», «Движение», …) — порядок показа. */
export const SCENE_TRANSITION_GROUPS = transitionsSpec.groups as { key: string; label: string }[]
export const SCENE_TRANSITION_DURATIONS = transitionsSpec.durations as number[]
export const SCENE_TRANSITION_MIN = transitionsSpec.min_seconds as number

/** Сохранённый переход шота к следующему клипу — только по спеке; иное — встык (null). */
export function transitionOf(shot: SceneShot): SceneTransition | null {
	const t = shot.video_settings.transition
	if (!t || !SCENE_TRANSITIONS.some((x) => x.key === t.type) || !Number.isFinite(t.duration) || t.duration <= 0) return null
	return { type: t.type, duration: t.duration }
}
